import { AlertTriangle, ArrowRight, X } from "react-feather";
import type { SwitchProgramSummary } from "@/services/program.service";
import { Button } from "@/components/ui/button";

interface SwitchProgramModalProps {
  isOpen: boolean;
  brandName: string;
  /** The program they're in today. */
  from: SwitchProgramSummary;
  /** The program the share link points at. */
  to: SwitchProgramSummary;
  /** Manual-approval targets don't move them until the brand approves. */
  requiresApproval: boolean;
  isSubmitting?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function commissionLabel(program: SwitchProgramSummary) {
  return program.commissionType === "FIXED"
    ? `$${Number(program.commissionValue)} per sale`
    : `${Number(program.commissionValue)}% commission`;
}

/**
 * What the affiliate gives up by following a share link to another program.
 *
 * An affiliate belongs to one program per brand, so joining a second is a move,
 * not an addition. The consequences differ by direction and are not obvious —
 * a sneakylink affiliate loses every link they have ever shared — so this spells
 * them out before anything is submitted.
 */
export function SwitchProgramModal({
  isOpen,
  brandName,
  from,
  to,
  requiresApproval,
  isSubmitting,
  onConfirm,
  onClose,
}: SwitchProgramModalProps) {
  if (!isOpen) return null;

  const losesSneakylinks = from.attributionMethod === "SNEAKYLINK";
  const losesCode =
    from.attributionMethod === "STATIC_CODE" &&
    to.attributionMethod === "SNEAKYLINK";
  const keepsCode =
    from.attributionMethod === "STATIC_CODE" &&
    to.attributionMethod === "STATIC_CODE";
  const balance = Number(from.unpaidBalance ?? 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-card shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                You're switching programs
              </h2>
              <p className="text-sm text-muted-foreground">
                You can only be in one {brandName} program at a time.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-muted-foreground hover:text-muted-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="flex items-stretch gap-3">
            <div className="min-w-0 flex-1 rounded-xl border border-border bg-muted p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Leaving
              </p>
              <p className="truncate font-semibold text-foreground">{from.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {commissionLabel(from)}
              </p>
              <p className="text-sm text-muted-foreground">
                {Number(from.discountValue)}% customer discount
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {from.attributionMethod === "SNEAKYLINK"
                  ? "Sneakylink"
                  : `Code ${from.code || "—"}`}
              </p>
            </div>

            <div className="flex items-center">
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>

            <div className="min-w-0 flex-1 rounded-xl border-2 border-brand-200 bg-brand-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-500">
                Joining
              </p>
              <p className="truncate font-semibold text-foreground">{to.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{commissionLabel(to)}</p>
              <p className="text-sm text-muted-foreground">
                {Number(to.discountValue)}% customer discount
              </p>
              <p className="mt-1 text-xs text-brand-500">
                {to.attributionMethod === "SNEAKYLINK"
                  ? "Sneakylink"
                  : "Discount code"}
              </p>
            </div>
          </div>

          <ul className="space-y-2 text-sm text-muted-foreground">
            {balance > 0 && (
              <li className="flex gap-2">
                <span className="text-green-600">✓</span>
                <span>
                  Your unpaid balance of{" "}
                  <span className="font-medium text-foreground">
                    ${balance.toFixed(2)}
                  </span>{" "}
                  moves with you. Past earnings aren't affected.
                </span>
              </li>
            )}
            {keepsCode && (
              <li className="flex gap-2">
                <span className="text-green-600">✓</span>
                <span>
                  You keep the code{" "}
                  <span className="font-medium text-foreground">{from.code}</span>
                  , but it will give customers{" "}
                  {Number(to.discountValue)}% instead of{" "}
                  {Number(from.discountValue)}%.
                </span>
              </li>
            )}
            {losesSneakylinks && (
              <li className="flex gap-2">
                <span className="text-amber-600">!</span>
                <span>
                  <span className="font-medium text-foreground">
                    Every sneakylink you've shared will stop working.
                  </span>{" "}
                  You'll get a new one and will need to re-share it.
                </span>
              </li>
            )}
            {losesCode && (
              <li className="flex gap-2">
                <span className="text-amber-600">!</span>
                <span>
                  Your discount code{" "}
                  <span className="font-medium text-foreground">{from.code}</span>{" "}
                  will stop working. You'll get a sneakylink instead.
                </span>
              </li>
            )}
            {requiresApproval && (
              <li className="flex gap-2">
                <span className="text-blue-600">i</span>
                <span>
                  {brandName} approves applications manually, so{" "}
                  <span className="font-medium text-foreground">
                    nothing changes yet
                  </span>
                  . You stay in {from.name} and keep earning until they approve.
                  If they decline, you stay where you are.
                </span>
              </li>
            )}
          </ul>
        </div>

        <div className="flex justify-end gap-3 border-t border-border p-5">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg bg-muted px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            Stay in {from.name}
          </button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            {requiresApproval ? "Apply to switch" : `Switch to ${to.name}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SwitchProgramModal;
